import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  Divider,
  Paper,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Article as ArticleIcon,
  Mic as MicIcon,
  Audiotrack as AudiotrackIcon,
  Settings as SettingsIcon,
  DragIndicator as DragIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

import { apiService } from '../services/api';
import {
  Episode,
  Show,
  Channel,
  ShowSegment,
  Panelist,
  NewsArticle,
  NewsItem,
  SourceMaterial,
  ExternalNewsArticle,
} from '../services/api';
import Breadcrumb from './Breadcrumb';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`episode-tabpanel-${index}`}
      aria-labelledby={`episode-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface BackgroundTask {
  type: 'script' | 'audio' | 'search';
  message: string;
  progress?: number;
  estimatedDuration?: number;
  status?: 'running' | 'completed' | 'failed' | 'cancelled';
}

const EpisodeDetail: React.FC = () => {
  const { episodeId } = useParams<{ episodeId: string }>();
  const navigate = useNavigate();
  
  const [episode, setEpisode] = useState<Episode | null>(null);
  const [show, setShow] = useState<Show | null>(null);
  const [channel, setChannel] = useState<Channel | null>(null);
  const [segments, setSegments] = useState<ShowSegment[]>([]);
  const [panelists, setPanelists] = useState<Panelist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showCreateSegmentDialog, setShowCreateSegmentDialog] = useState(false);
  const [showAddNewsDialog, setShowAddNewsDialog] = useState(false);
  const [openSourceDialog, setOpenSourceDialog] = useState(false);
  const [openScriptDialog, setOpenScriptDialog] = useState(false);
  const [openPanelistDialog, setOpenPanelistDialog] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<ShowSegment | null>(null);
  const [showNewsSearchModal, setShowNewsSearchModal] = useState(false);
  const [currentSearchSegment, setCurrentSearchSegment] = useState<ShowSegment | null>(null);
  const [selectedNewsItem, setSelectedNewsItem] = useState<NewsItem | null>(null);
  const [segmentMenuAnchor, setSegmentMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedSegmentForMenu, setSelectedSegmentForMenu] = useState<ShowSegment | null>(null);
  const [draggedSegment, setDraggedSegment] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [segmentNewsItems, setSegmentNewsItems] = useState<{ [segmentId: string]: NewsItem[] }>({});
  const [articleDetails, setArticleDetails] = useState<{ [articleId: string]: NewsArticle }>({});
  const [sourceMaterials, setSourceMaterials] = useState<SourceMaterial[]>([]);
  const [searchResults, setSearchResults] = useState<(NewsArticle | ExternalNewsArticle)[]>([]);
  const [externalSearchResults, setExternalSearchResults] = useState<ExternalNewsArticle[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'internal' | 'external'>('internal');
  const [searchCategories, setSearchCategories] = useState('');
  const [searchPublishedOn, setSearchPublishedOn] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [backgroundTasks, setBackgroundTasks] = useState<{ [taskId: string]: BackgroundTask }>({});
  const [audioState, setAudioState] = useState({
    isPlaying: false,
    segmentId: '',
    currentPart: 0,
    totalParts: 0,
    audioElement: null as HTMLAudioElement | null,
  });
  const stopPlaylistRef = useRef(false);

  // Episode editing state
  const [openEpisodeDialog, setOpenEpisodeDialog] = useState(false);
  const [episodeFormData, setEpisodeFormData] = useState({
    title: '',
    description: '',
    episodeNumber: undefined as number | undefined,
    seasonNumber: undefined as number | undefined,
    status: 'draft' as 'draft' | 'published' | 'archived',
    isPublic: true,
  });

  // Form states
  const [newSegmentTitle, setNewSegmentTitle] = useState('');
  const [newSegmentDescription, setNewSegmentDescription] = useState('');
  
  // Enhanced segment creation states
  const [segmentCreationStep, setSegmentCreationStep] = useState<'search' | 'details'>('search');
  const [selectedArticleForSegment, setSelectedArticleForSegment] = useState<NewsArticle | null>(null);
  const [segmentSearchQuery, setSegmentSearchQuery] = useState('');
  const [segmentSearchResults, setSegmentSearchResults] = useState<NewsArticle[]>([]);
  const [segmentSearchLoading, setSegmentSearchLoading] = useState(false);
  const [segmentOriginalExternalArticles, setSegmentOriginalExternalArticles] = useState<ExternalNewsArticle[]>([]);
  const [segmentSearchFilters, setSegmentSearchFilters] = useState({
    language: 'en',
    categories: '',
    publishedAfter: '',
    publishedBefore: '',
    limit: 20,
  });
  const [showSegmentAdvancedFilters, setShowSegmentAdvancedFilters] = useState(false);
  const [sourceTitle, setSourceTitle] = useState('');
  const [sourceContent, setSourceContent] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');

  useEffect(() => {
    console.log('EpisodeDetail useEffect triggered, episodeId:', episodeId);
    if (episodeId) {
      console.log('Fetching episode data for ID:', episodeId);
      fetchEpisodeData();
    } else {
      console.log('No episodeId found in URL params');
    }
  }, [episodeId]);

  // Cleanup audio when component unmounts
  useEffect(() => {
    return () => {
      if (audioState.audioElement) {
        audioState.audioElement.pause();
        audioState.audioElement.currentTime = 0;
      }
    };
  }, [audioState.audioElement]);

  const fetchEpisodeData = async () => {
    try {
      console.log('Starting fetchEpisodeData for episodeId:', episodeId);
      setLoading(true);
      setError('');

      // First get the episode data
      console.log('Calling apiService.getEpisode with ID:', episodeId);
      const episodeData = await apiService.getEpisode(episodeId!);
      console.log('Episode data received:', episodeData);
      setEpisode(episodeData);

      // Then get related data
      const [showData, channelData, segmentsData, panelistsData] = await Promise.all([
        apiService.getShow(episodeData.showId),
        apiService.getChannel(episodeData.channelId),
        apiService.getShowSegments(episodeData._id),
        apiService.getPanelists(),
      ]);

      setShow(showData);
      setChannel(channelData);
      setSegments(segmentsData);
      setPanelists(panelistsData);

      // Fetch news items for each segment
      const newsItemsData = await Promise.all(
        segmentsData.map(segment => apiService.getSegmentNewsItems(segment._id))
      );

      const segmentNewsMap: { [segmentId: string]: NewsItem[] } = {};
      segmentsData.forEach((segment, index) => {
        segmentNewsMap[segment._id] = newsItemsData[index];
      });
      setSegmentNewsItems(segmentNewsMap);

    } catch (error) {
      console.error('Error fetching episode data:', error);
      setError('Failed to load episode data');
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const createTaskId = (type: string, id: string) => `${type}_${id}_${Date.now()}`;

  const addBackgroundTask = (taskId: string, task: BackgroundTask) => {
    setBackgroundTasks(prev => ({
      ...prev,
      [taskId]: { ...task, status: 'running' }
    }));
  };

  const updateBackgroundTask = (taskId: string, updates: Partial<BackgroundTask>) => {
    setBackgroundTasks(prev => ({
      ...prev,
      [taskId]: { ...prev[taskId], ...updates }
    }));
  };

  const removeBackgroundTask = (taskId: string) => {
    setBackgroundTasks(prev => {
      const newTasks = { ...prev };
      delete newTasks[taskId];
      return newTasks;
    });
  };

  const handleCreateSegment = async () => {
    if (!newSegmentTitle.trim() || !episode) return;

    try {
      const newSegment = await apiService.createShowSegment({
        episodeId: episode._id, // Use episode ID for segments
        title: newSegmentTitle,
        description: newSegmentDescription,
        order: segments.length + 1,
      });

      // If we have a selected article, add it to the segment
      if (selectedArticleForSegment) {
        try {
          // Check if this is an external article by looking for it in the original external articles
          const originalExternalArticle = segmentOriginalExternalArticles.find(ext => ext.uuid === selectedArticleForSegment._id);
          
          if (originalExternalArticle) {
            // This is an external article, we need to ingest it first
            console.log('Ingesting external article for segment:', selectedArticleForSegment._id);
            
            // Ingest the external article into our database
            const ingestResponse = await apiService.ingestExternalArticle(selectedArticleForSegment._id, originalExternalArticle);
            
            console.log('Ingestion response:', ingestResponse);
            
            // Use the ingested article's ID
            const ingestedArticleId = ingestResponse.article_id;
            
            // Add the ingested article to the segment
            await apiService.addNewsItemToSegment(newSegment._id, {
              newsArticleId: ingestedArticleId,
              sourceMaterials: [],
            });
            
            console.log('External news article ingested and added to segment successfully');
            setSuccessMessage('Segment created with external news article successfully');
          } else {
            // This is an internal article, add it directly
            await apiService.addNewsItemToSegment(newSegment._id, {
              newsArticleId: selectedArticleForSegment._id,
              sourceMaterials: [],
            });
            console.log('Internal news item added to segment successfully');
            setSuccessMessage('Segment created with news article successfully');
          }
          setShowSuccessMessage(true);
        } catch (addError) {
          console.error('Error adding news item to segment:', addError);
          // Don't fail the entire operation if adding news fails
        }
      } else {
        // No article selected, just create the segment
        setSuccessMessage('Segment created successfully');
        setShowSuccessMessage(true);
      }

      setSegments(prev => [...prev, newSegment]);
      setShowCreateSegmentDialog(false);
      setNewSegmentTitle('');
      setNewSegmentDescription('');
      setSelectedArticleForSegment(null);
      setSegmentCreationStep('search');
      setSegmentSearchQuery('');
      setSegmentSearchResults([]);
      setSegmentOriginalExternalArticles([]);
    } catch (error) {
      console.error('Error creating segment:', error);
      setError('Failed to create segment');
    }
  };

  const handleSegmentSearch = async () => {
    if (!segmentSearchQuery.trim()) return;

    try {
      setSegmentSearchLoading(true);
      setError('');
      
      const searchParams = {
        query: segmentSearchQuery.trim(),
        language: segmentSearchFilters.language,
        limit: segmentSearchFilters.limit,
        ...(segmentSearchFilters.categories && { category: segmentSearchFilters.categories }),
        ...(segmentSearchFilters.publishedAfter && { published_after: segmentSearchFilters.publishedAfter }),
        ...(segmentSearchFilters.publishedBefore && { published_before: segmentSearchFilters.publishedBefore }),
      };
      
      const results = await apiService.searchExternalNews(searchParams);
      // Store original external articles for ingestion
      setSegmentOriginalExternalArticles(results);
      
      setSegmentSearchResults(results.map(article => ({
        _id: article.uuid,
        title: article.title,
        description: article.description,
        content: article.content,
        fullContents: article.content,
        url: article.url,
        urlToImage: article.image_url || '',
        publishedAt: article.published_at,
        source: { 
          id: article.source_id || article.source,
          name: article.source 
        },
        category: article.categories?.[0] || 'general',
        language: article.language,
        contentExtractedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })));
      
      if (results.length === 0) {
        // Fallback to internal news search if no external results
        const internalResults = await apiService.searchNews(segmentSearchQuery.trim(), 20);
        setSegmentSearchResults(internalResults);
        setSegmentOriginalExternalArticles([]); // Clear external articles for internal results
      }
    } catch (error) {
      console.error('Error searching news for segment:', error);
      setError('Failed to search news. Please try again.');
      setSegmentSearchResults([]);
    } finally {
      setSegmentSearchLoading(false);
    }
  };

  const handleSelectArticleForSegment = (article: NewsArticle) => {
    setSelectedArticleForSegment(article);
    setSegmentCreationStep('details');
    // Pre-fill title and description based on the article
    setNewSegmentTitle(article.title);
    setNewSegmentDescription(article.description || '');
  };

  const handleBackToSearch = () => {
    setSegmentCreationStep('search');
    setSelectedArticleForSegment(null);
  };

  const handleCloseCreateSegmentDialog = () => {
    setShowCreateSegmentDialog(false);
    setSegmentCreationStep('search');
    setSelectedArticleForSegment(null);
    setNewSegmentTitle('');
    setNewSegmentDescription('');
    setSegmentSearchQuery('');
    setSegmentSearchResults([]);
    setSegmentOriginalExternalArticles([]);
    setShowSegmentAdvancedFilters(false);
    setSegmentSearchFilters({
      language: 'en',
      categories: '',
      publishedAfter: '',
      publishedBefore: '',
      limit: 20,
    });
  };

  const handleDeleteSegment = async (segmentId: string) => {
    try {
      await apiService.deleteShowSegment(segmentId);
      setSegments(prev => prev.filter(s => s._id !== segmentId));
    } catch (error) {
      console.error('Error deleting segment:', error);
      setError('Failed to delete segment');
    }
  };

  const handleOpenSegmentMenu = (event: React.MouseEvent<HTMLElement>, segment: ShowSegment) => {
    setSegmentMenuAnchor(event.currentTarget);
    setSelectedSegmentForMenu(segment);
  };

  const handleCloseSegmentMenu = () => {
    setSegmentMenuAnchor(null);
    setSelectedSegmentForMenu(null);
  };

  const handleDeleteSegmentFromMenu = () => {
    if (selectedSegmentForMenu) {
      if (window.confirm(`Are you sure you want to delete the segment "${selectedSegmentForMenu.title}"?`)) {
        handleDeleteSegment(selectedSegmentForMenu._id);
      }
      handleCloseSegmentMenu();
    }
  };

  const handleDragStart = (e: React.DragEvent, segmentId: string) => {
    setDraggedSegment(segmentId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetSegmentId: string) => {
    e.preventDefault();
    
    if (!draggedSegment || draggedSegment === targetSegmentId) {
      setDraggedSegment(null);
      return;
    }

    const draggedIndex = segments.findIndex(segment => segment._id === draggedSegment);
    const targetIndex = segments.findIndex(segment => segment._id === targetSegmentId);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedSegment(null);
      return;
    }

    const newSegments = Array.from(segments);
    const [reorderedSegment] = newSegments.splice(draggedIndex, 1);
    newSegments.splice(targetIndex, 0, reorderedSegment);

    // Update the order property for each segment
    const updatedSegments = newSegments.map((segment, index) => ({
      ...segment,
      order: index + 1,
    }));

    setSegments(updatedSegments);
    setDraggedSegment(null);

    try {
      // Update segment orders in the backend
      await Promise.all(
        updatedSegments.map(segment => 
          apiService.updateShowSegment(segment._id, { order: segment.order })
        )
      );
      setSuccessMessage('Segments reordered successfully');
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error reordering segments:', error);
      setError('Failed to reorder segments');
      // Revert to original order if API call failed
      fetchEpisodeData();
    }
  };

  const handleDragEnter = (e: React.DragEvent, segmentId: string) => {
    e.preventDefault();
    if (draggedSegment && draggedSegment !== segmentId) {
      // Add visual feedback for drop zone
      const target = e.currentTarget as HTMLElement;
      target.style.borderColor = 'primary.main';
      target.style.borderWidth = '2px';
      target.style.borderStyle = 'dashed';
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    // Remove visual feedback
    const target = e.currentTarget as HTMLElement;
    target.style.borderColor = '';
    target.style.borderWidth = '';
    target.style.borderStyle = '';
  };

  const handleOpenNewsSearch = (segment: ShowSegment) => {
    setCurrentSearchSegment(segment);
    setShowNewsSearchModal(true);
    setSearchResults([]);
    setExternalSearchResults([]);
    setSearchQuery('');
    setSearchType('internal');
    setSearchCategories('');
    setSearchPublishedOn('');
  };

  const handleSearchNews = async () => {
    if (!searchQuery.trim()) return;

    setSearchLoading(true);
    try {
      if (searchType === 'internal') {
        const results = await apiService.searchNews(searchQuery);
        setSearchResults(results);
      } else {
        const externalResults = await apiService.searchExternalNews({
          query: searchQuery,
          category: searchCategories || undefined,
          published_after: searchPublishedOn || undefined,
        });
        setExternalSearchResults(externalResults);
        setSearchResults(externalResults);
      }
    } catch (error) {
      console.error('Error searching news:', error);
      setError('Failed to search news');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddNewsToSegment = async (segmentId: string, article: NewsArticle | ExternalNewsArticle) => {
    try {
      await apiService.addNewsItemToSegment(segmentId, {
        title: article.title,
        description: article.description,
        content: article.content,
        source: typeof article.source === 'string' ? article.source : article.source.name,
        source_id: typeof article.source === 'string' ? '' : article.source.id,
        url: article.url,
        publishedAt: 'publishedAt' in article ? article.publishedAt : article.published_at,
        image_url: 'image_url' in article ? article.image_url : ('urlToImage' in article ? article.urlToImage : ''),
        category: 'categories' in article ? article.categories?.[0] || 'general' : article.category,
        language: article.language || 'en',
      });

      // Refresh segment news items
      const updatedNewsItems = await apiService.getSegmentNewsItems(segmentId);
      setSegmentNewsItems(prev => ({
        ...prev,
        [segmentId]: updatedNewsItems,
      }));

      // Show success message
      setError(''); // Clear any previous errors
      setSuccessMessage(`"${article.title}" added to segment successfully`);
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error adding news to segment:', error);
      setError('Failed to add news to segment');
    }
  };

  const handleGenerateScript = async (segmentId: string) => {
    const taskId = createTaskId('script', segmentId);
    addBackgroundTask(taskId, {
      type: 'script',
      message: 'Generating script...',
      estimatedDuration: 30000,
    });

    try {
      await apiService.generateScript(segmentId, {
        showSegmentId: segmentId,
        episodeId: episode?._id || '',
        requestedBy: 'current-user',
        provider: 'openai', // Default to OpenAI for EpisodeDetail
      });

      updateBackgroundTask(taskId, { progress: 50, message: 'Script generation started...' });

      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const segment = await apiService.getShowSegment(segmentId);
          if (segment.script || segment.scriptParts.length > 0) {
            setSegments(prev => prev.map(s => s._id === segmentId ? segment : s));
            updateBackgroundTask(taskId, { progress: 100, message: 'Script generated successfully', status: 'completed' });
            clearInterval(pollInterval);
            setTimeout(() => removeBackgroundTask(taskId), 3000);
          }
        } catch (error) {
          console.error('Error polling script status:', error);
        }
      }, 2000);

    } catch (error) {
      console.error('Error generating script:', error);
      updateBackgroundTask(taskId, { message: 'Script generation failed', status: 'failed' });
      setTimeout(() => removeBackgroundTask(taskId), 5000);
    }
  };

  const handleGenerateAudio = async (segmentId: string) => {
    const taskId = createTaskId('audio', segmentId);
    addBackgroundTask(taskId, {
      type: 'audio',
      message: 'Generating audio...',
      estimatedDuration: 60000,
    });

    try {
      await apiService.requestAudioGeneration(segmentId);

      updateBackgroundTask(taskId, { progress: 30, message: 'Audio generation started...' });

      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const segment = await apiService.getShowSegment(segmentId);
          if (segment.audioGenerationStatus === 'completed') {
            setSegments(prev => prev.map(s => s._id === segmentId ? segment : s));
            updateBackgroundTask(taskId, { progress: 100, message: 'Audio generated successfully', status: 'completed' });
            clearInterval(pollInterval);
            setTimeout(() => removeBackgroundTask(taskId), 3000);
          } else if (segment.audioGenerationStatus === 'failed') {
            updateBackgroundTask(taskId, { message: 'Audio generation failed', status: 'failed' });
            clearInterval(pollInterval);
            setTimeout(() => removeBackgroundTask(taskId), 5000);
          }
        } catch (error) {
          console.error('Error polling audio status:', error);
        }
      }, 3000);

    } catch (error) {
      console.error('Error generating audio:', error);
      updateBackgroundTask(taskId, { message: 'Audio generation failed', status: 'failed' });
      setTimeout(() => removeBackgroundTask(taskId), 5000);
    }
  };

  const handlePlayAudioForSegment = async (segment: ShowSegment) => {
    if (!segment) return;

    const taskId = createTaskId('audio', segment._id);
    addBackgroundTask(taskId, {
      type: 'audio',
      message: 'Preparing audio playback...',
      estimatedDuration: 5000,
    });

    try {
      // First fetch a fresh copy in case URL landed after list load
      const freshSegment = await apiService.getShowSegment(segment._id);
      
      // Update the segments array with fresh data
      setSegments(prev => prev.map(s => s._id === segment._id ? freshSegment : s));
      
      // Check for script parts with audio URLs
      const freshScriptPartsWithAudio = freshSegment.scriptParts.filter((part: any) => part.audioUrl);
      if (freshScriptPartsWithAudio.length > 0) {
        // Audio is ready, start playback
        updateBackgroundTask(taskId, { progress: 100, message: 'Audio ready for playback' });
        removeBackgroundTask(taskId);
        
        // Start complete segment audio playback
        await playCompleteSegmentAudio(freshSegment);
        return;
      }
      
      // If still no audio URLs, trigger generation
      await handleGenerateAudio(segment._id);
      
    } catch (error) {
      console.error('Error preparing audio:', error);
      updateBackgroundTask(taskId, { message: 'Failed to prepare audio', status: 'failed' });
      setTimeout(() => removeBackgroundTask(taskId), 5000);
    }
  };

  const playCompleteSegmentAudio = async (segment: ShowSegment) => {
    if (!segment.scriptParts || segment.scriptParts.length === 0) {
      console.log('No script parts to play');
      return;
    }

    // Stop any currently playing audio
    if (audioState.audioElement) {
      audioState.audioElement.pause();
      audioState.audioElement.currentTime = 0;
    }

    // Set up audio state for this segment
    setAudioState({
      isPlaying: true,
      segmentId: segment._id,
      currentPart: 0,
      totalParts: segment.scriptParts.length,
      audioElement: null,
    });

    // Play each script part in sequence
    for (let i = 0; i < segment.scriptParts.length; i++) {
      const part = segment.scriptParts[i];
      
      if (part.audioUrl) {
        try {
          // Get audio data from the API
          const audioBlob = await apiService.getAudioFile(segment._id, i);
          const audioUrl = URL.createObjectURL(audioBlob);
          
          // Create audio element
          const audio = new Audio(audioUrl);
          
          // Update audio state
          setAudioState(prev => ({
            ...prev,
            currentPart: i,
            audioElement: audio,
          }));

          // Play the audio part
          await new Promise((resolve, reject) => {
            audio.onended = () => {
              URL.revokeObjectURL(audioUrl);
              resolve(void 0);
            };
            audio.onerror = (error) => {
              URL.revokeObjectURL(audioUrl);
              reject(error);
            };
            audio.play().catch(reject);
          });

        } catch (error) {
          console.error(`Error playing audio part ${i}:`, error);
        }
      }
    }

    // Reset audio state when done
    setAudioState({
      isPlaying: false,
      segmentId: '',
      currentPart: 0,
      totalParts: 0,
      audioElement: null,
    });
  };

  const handlePlayAudioPart = async (segment: ShowSegment, partIndex: number) => {
    if (!segment.scriptParts || !segment.scriptParts[partIndex]) {
      console.log('Script part not found');
      return;
    }

    const part = segment.scriptParts[partIndex];
    
    if (!part.audioUrl) {
      console.log('No audio URL for this part');
      return;
    }

    try {
      // Stop any currently playing audio
      if (audioState.audioElement) {
        audioState.audioElement.pause();
        audioState.audioElement.currentTime = 0;
      }

      // Get audio data from the API
      const audioBlob = await apiService.getAudioFile(segment._id, partIndex);
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Create audio element
      const audio = new Audio(audioUrl);
      
      // Set up audio state
      setAudioState({
        isPlaying: true,
        segmentId: segment._id,
        currentPart: partIndex,
        totalParts: segment.scriptParts.length,
        audioElement: audio,
      });

      // Play the audio
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        setAudioState(prev => ({
          ...prev,
          isPlaying: false,
        }));
      };
      
      audio.onerror = (error) => {
        URL.revokeObjectURL(audioUrl);
        console.error('Error playing audio:', error);
        setAudioState(prev => ({
          ...prev,
          isPlaying: false,
        }));
      };

      await audio.play();
      
    } catch (error) {
      console.error('Error playing audio part:', error);
    }
  };

  const handleStopAudio = () => {
    if (audioState.audioElement) {
      audioState.audioElement.pause();
      audioState.audioElement.currentTime = 0;
    }
    
    setAudioState({
      isPlaying: false,
      segmentId: '',
      currentPart: 0,
      totalParts: 0,
      audioElement: null,
    });
  };

  // Episode editing handlers
  const handleOpenEpisodeDialog = () => {
    if (episode) {
      setEpisodeFormData({
        title: episode.title,
        description: episode.description || '',
        episodeNumber: episode.episodeNumber,
        seasonNumber: episode.seasonNumber,
        status: episode.status as 'draft' | 'published' | 'archived',
        isPublic: episode.isPublic,
      });
    }
    setOpenEpisodeDialog(true);
  };

  const handleCloseEpisodeDialog = () => {
    setOpenEpisodeDialog(false);
  };

  const handleEpisodeSubmit = async () => {
    if (!episode) return;

    try {
      const updatedEpisode = await apiService.updateEpisode(episode._id, episodeFormData);
      setEpisode(updatedEpisode);
      setOpenEpisodeDialog(false);
      setSuccessMessage('Episode updated successfully!');
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error updating episode:', error);
      setError('Failed to update episode');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading episode...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!episode) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Episode not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={[
        { label: 'Channels', path: '/channels', icon: <ArticleIcon /> },
        { label: channel?.name || 'Channel', path: `/channels/${channel?._id}`, icon: <ArticleIcon /> },
        { label: show?.name || 'Show', path: `/shows/${show?._id}`, icon: <ArticleIcon /> },
        { label: episode.title, path: `/episodes/${episode._id}`, icon: <ArticleIcon /> }
      ]} />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            {episode.title}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Show: {show?.name} • Channel: {channel?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {episode.description}
          </Typography>
        </Box>
        <Box>
          <Chip 
            label={episode.status} 
            color={episode.status === 'published' ? 'success' : 'default'} 
            sx={{ mr: 2 }}
          />
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleOpenEpisodeDialog}
          >
            Edit Episode
          </Button>
        </Box>
      </Box>

      {/* Audio Status */}
      {audioState.isPlaying && (
        <Alert severity="info" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PlayIcon color="primary" />
            <Typography variant="body2">
              Playing audio: {segments.find(s => s._id === audioState.segmentId)?.title || 'Unknown Segment'}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Part {audioState.currentPart + 1} of {audioState.totalParts}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<StopIcon />}
            onClick={handleStopAudio}
            sx={{ ml: 'auto' }}
          >
            Stop
          </Button>
        </Alert>
      )}

      {/* Background Tasks */}
      {Object.entries(backgroundTasks).map(([taskId, task]) => (
        <Alert key={taskId} severity="info" sx={{ mb: 2 }}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="body2">{task.message}</Typography>
            {task.progress !== undefined && (
              <LinearProgress 
                variant="determinate" 
                value={task.progress} 
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        </Alert>
      ))}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Segments" />
          <Tab label="Scripts" />
          <Tab label="Audio" />
        </Tabs>
      </Box>

      {/* Segments Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateSegmentDialog(true)}
            >
              Add Segment
            </Button>
            <Button
              variant="outlined"
              startIcon={<DragIcon />}
              onClick={() => {
                // This will enable visual feedback that segments can be reordered
                setSuccessMessage('Drag and drop segments to reorder them');
                setShowSuccessMessage(true);
              }}
            >
              Reorder Segments
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary">
            💡 Drag and drop segments to reorder them. The order will be saved automatically.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {segments.map((segment) => (
            <Grid item xs={12} key={segment._id}>
              <Card
                draggable
                onDragStart={(e) => handleDragStart(e, segment._id)}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, segment._id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, segment._id)}
                sx={{
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' },
                  opacity: draggedSegment === segment._id ? 0.5 : 1,
                  transform: draggedSegment === segment._id ? 'scale(0.98)' : 'scale(1)',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <DragIcon 
                          sx={{ 
                            color: 'text.secondary', 
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' }
                          }} 
                        />
                        <Typography variant="h6">{segment.title}</Typography>
                        <Chip 
                          label={`Order: ${segment.order || 'N/A'}`}
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {segment.description}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip 
                          label={segment.status} 
                          size="small" 
                          color={segment.status === 'published' ? 'success' : 'default'}
                          sx={{ mr: 1 }}
                        />
                        <Chip 
                          label={`${segmentNewsItems[segment._id]?.length || 0} news items`}
                          size="small"
                          variant="outlined"
                          color={segmentNewsItems[segment._id]?.length > 0 ? 'primary' : 'default'}
                        />
                      </Box>
                      
                      {/* Show news items preview */}
                      {segmentNewsItems[segment._id]?.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Recent: {segmentNewsItems[segment._id].slice(0, 2).map(item => item.article?.title || 'Unknown Article').join(', ')}
                            {segmentNewsItems[segment._id].length > 2 && '...'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Manage Segment">
                        <IconButton 
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/episodes/${episodeId}/segments/${segment._id}`)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Search & Add News">
                        <IconButton 
                          size="small"
                          onClick={() => handleOpenNewsSearch(segment)}
                        >
                          <SearchIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Generate Script">
                        <span>
                          <IconButton 
                            size="small"
                            onClick={() => handleGenerateScript(segment._id)}
                            disabled={!segmentNewsItems[segment._id]?.length}
                          >
                            <MicIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Generate Audio">
                        <span>
                          <IconButton 
                            size="small"
                            onClick={() => handleGenerateAudio(segment._id)}
                            disabled={!segment.script && !segment.scriptParts.length}
                          >
                            <AudiotrackIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={segment.audioGenerationStatus !== 'completed' ? 'Audio not ready' : 'Play Complete Segment'}>
                        <span>
                          <IconButton 
                            size="small"
                            onClick={() => handlePlayAudioForSegment(segment)}
                            disabled={segment.audioGenerationStatus !== 'completed'}
                            color={audioState.isPlaying && audioState.segmentId === segment._id ? 'primary' : 'default'}
                          >
                            <PlayIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="More Actions">
                        <IconButton 
                          size="small"
                          onClick={(e) => handleOpenSegmentMenu(e, segment)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Segment Actions Menu */}
      <Menu
        anchorEl={segmentMenuAnchor}
        open={Boolean(segmentMenuAnchor)}
        onClose={handleCloseSegmentMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleDeleteSegmentFromMenu}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          Delete Segment
        </MenuItem>
      </Menu>

      {/* Scripts Tab */}
      <TabPanel value={tabValue} index={1}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Scripts and Content
        </Typography>
        <Grid container spacing={2}>
          {segments.map((segment) => (
            <Grid item xs={12} key={segment._id}>
              <Card
                draggable
                onDragStart={(e) => handleDragStart(e, segment._id)}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, segment._id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, segment._id)}
                sx={{
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' },
                  opacity: draggedSegment === segment._id ? 0.5 : 1,
                  transform: draggedSegment === segment._id ? 'scale(0.98)' : 'scale(1)',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <DragIcon 
                      sx={{ 
                        color: 'text.secondary', 
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' }
                      }} 
                    />
                    <Typography variant="h6">{segment.title}</Typography>
                    <Chip 
                      label={`Order: ${segment.order || 'N/A'}`}
                      size="small"
                      variant="outlined"
                      color="default"
                    />
                  </Box>
                  {segment.script ? (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {segment.script}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No script generated yet
                    </Typography>
                  )}
                  {segment.scriptParts.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2">Script Parts:</Typography>
                      {segment.scriptParts.map((part, index) => (
                        <Box key={index} sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>
                              {part.panelistName}:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              {part.audioUrl ? (
                                <Tooltip title="Play this part">
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePlayAudioPart(segment, index)}
                                    disabled={audioState.isPlaying && audioState.segmentId === segment._id}
                                    color={audioState.isPlaying && audioState.segmentId === segment._id && audioState.currentPart === index ? 'primary' : 'default'}
                                  >
                                    <PlayIcon />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title="No audio available for this part">
                                  <IconButton size="small" disabled>
                                    <PlayIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                Part {index + 1}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body2">{part.script}</Typography>
                          {part.audioUrl && (
                            <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                              ✓ Audio available
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Audio Tab */}
      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Audio Generation
        </Typography>
        <Grid container spacing={2}>
          {segments.map((segment) => (
            <Grid item xs={12} key={segment._id}>
              <Card
                draggable
                onDragStart={(e) => handleDragStart(e, segment._id)}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, segment._id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, segment._id)}
                sx={{
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' },
                  opacity: draggedSegment === segment._id ? 0.5 : 1,
                  transform: draggedSegment === segment._id ? 'scale(0.98)' : 'scale(1)',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <DragIcon 
                          sx={{ 
                            color: 'text.secondary', 
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' }
                          }} 
                        />
                        <Typography variant="h6">{segment.title}</Typography>
                        <Chip 
                          label={`Order: ${segment.order || 'N/A'}`}
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Status: {segment.audioGenerationStatus}
                      </Typography>
                      {segment.audioDuration && (
                        <Typography variant="body2" color="text.secondary">
                          Duration: {Math.round(segment.audioDuration)}s
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {segment.audioGenerationStatus === 'completed' ? (
                        <Chip label="Ready" color="success" icon={<CheckCircleIcon />} />
                      ) : segment.audioGenerationStatus === 'failed' ? (
                        <Chip label="Failed" color="error" icon={<ErrorIcon />} />
                      ) : segment.audioGenerationStatus === 'generating' ? (
                        <Chip label="Generating" color="warning" icon={<ScheduleIcon />} />
                      ) : (
                        <Chip label="Pending" color="default" />
                      )}
                      
                      {/* Audio Controls */}
                      {segment.audioGenerationStatus === 'completed' && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Play Complete Segment">
                            <Button
                              variant="outlined"
                              startIcon={<PlayIcon />}
                              onClick={() => handlePlayAudioForSegment(segment)}
                              disabled={audioState.isPlaying && audioState.segmentId === segment._id}
                              size="small"
                            >
                              Play All
                            </Button>
                          </Tooltip>
                          
                          {audioState.isPlaying && audioState.segmentId === segment._id && (
                            <Tooltip title="Stop Audio">
                              <Button
                                variant="outlined"
                                startIcon={<StopIcon />}
                                onClick={handleStopAudio}
                                color="error"
                                size="small"
                              >
                                Stop
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                      
                      {/* Audio Status Indicator */}
                      {audioState.isPlaying && audioState.segmentId === segment._id && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="primary">
                            Playing part {audioState.currentPart + 1} of {audioState.totalParts}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Create Segment Dialog */}
      <Dialog 
        open={showCreateSegmentDialog} 
        onClose={handleCloseCreateSegmentDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {segmentCreationStep === 'search' ? 'Create New Segment - Select News Article' : 'Create New Segment - Details'}
        </DialogTitle>
        <DialogContent>
          {segmentCreationStep === 'search' ? (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                First, search and select a news article for this segment. The article will be automatically added to the segment.
              </Typography>
              
              <TextField
                fullWidth
                label="Search for news articles"
                value={segmentSearchQuery}
                onChange={(e) => setSegmentSearchQuery(e.target.value)}
                placeholder="Enter keywords, title, or URL to search..."
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSegmentSearch}
                  disabled={!segmentSearchQuery.trim() || segmentSearchLoading}
                >
                  {segmentSearchLoading ? 'Searching...' : 'Search'}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => setShowSegmentAdvancedFilters(!showSegmentAdvancedFilters)}
                  size="small"
                >
                  {showSegmentAdvancedFilters ? 'Hide Filters' : 'Advanced Filters'}
                </Button>
              </Box>

              {showSegmentAdvancedFilters && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Advanced Search Filters
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Language"
                        value={segmentSearchFilters.language}
                        onChange={(e) => setSegmentSearchFilters(prev => ({ ...prev, language: e.target.value }))}
                        placeholder="en, es, fr, etc."
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Categories"
                        value={segmentSearchFilters.categories}
                        onChange={(e) => setSegmentSearchFilters(prev => ({ ...prev, categories: e.target.value }))}
                        placeholder="technology,science,business"
                        size="small"
                        helperText="Comma-separated categories"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Published After"
                        type="date"
                        value={segmentSearchFilters.publishedAfter}
                        onChange={(e) => setSegmentSearchFilters(prev => ({ ...prev, publishedAfter: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Published Before"
                        type="date"
                        value={segmentSearchFilters.publishedBefore}
                        onChange={(e) => setSegmentSearchFilters(prev => ({ ...prev, publishedBefore: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Limit"
                        type="number"
                        value={segmentSearchFilters.limit}
                        onChange={(e) => setSegmentSearchFilters(prev => ({ ...prev, limit: parseInt(e.target.value) || 20 }))}
                        inputProps={{ min: 1, max: 50 }}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {segmentSearchLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <LinearProgress sx={{ flex: 1 }} />
                  <Typography variant="body2">Searching...</Typography>
                </Box>
              )}

              {segmentSearchResults.length > 0 ? (
                <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                  {segmentSearchResults.map((article) => (
                    <Card key={article._id} sx={{ mb: 2, p: 2, cursor: 'pointer' }} onClick={() => handleSelectArticleForSegment(article)}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            {article.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {article.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip 
                              label={article.source?.name || 'Unknown Source'} 
                              size="small" 
                              variant="outlined"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {article.publishedAt && new Date(article.publishedAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ ml: 2 }}
                        >
                          Select
                        </Button>
                      </Box>
                    </Card>
                  ))}
                </Box>
              ) : segmentSearchQuery && !segmentSearchLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    No results found for "{segmentSearchQuery}"
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Try adjusting your search terms or filters
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography color="text.secondary">
                    Search for news articles to create a segment
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Enter search terms above to find relevant news
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ pt: 2 }}>
              {selectedArticleForSegment && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Selected Article:</strong> {selectedArticleForSegment.title}
                  </Typography>
                </Alert>
              )}
              
              <TextField
                autoFocus
                margin="dense"
                label="Segment Title"
                fullWidth
                variant="outlined"
                value={newSegmentTitle}
                onChange={(e) => setNewSegmentTitle(e.target.value)}
                sx={{ mb: 2 }}
                helperText="This will be the name of your segment"
              />
              <TextField
                margin="dense"
                label="Segment Description"
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                value={newSegmentDescription}
                onChange={(e) => setNewSegmentDescription(e.target.value)}
                helperText="Describe what this segment will cover"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {segmentCreationStep === 'details' && (
            <Button onClick={handleBackToSearch}>
              Back to Search
            </Button>
          )}
          <Button onClick={handleCloseCreateSegmentDialog}>Cancel</Button>
          {segmentCreationStep === 'details' && (
            <Button onClick={handleCreateSegment} variant="contained">
              Create Segment
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* News Search Modal */}
      <Dialog 
        open={showNewsSearchModal} 
        onClose={() => setShowNewsSearchModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Search & Add News to "{currentSearchSegment?.title}"
            </Typography>
            <IconButton onClick={() => setShowNewsSearchModal(false)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', height: '100%' }}>
            {/* Search Panel */}
            <Box sx={{ width: '40%', borderRight: 1, borderColor: 'divider', p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Search News</Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Search Query"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchNews()}
                    placeholder="Enter search terms..."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Search Type</InputLabel>
                    <Select
                      value={searchType}
                      label="Search Type"
                      onChange={(e) => setSearchType(e.target.value as 'internal' | 'external')}
                    >
                      <MenuItem value="internal">Internal News</MenuItem>
                      <MenuItem value="external">External News</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Category"
                    value={searchCategories}
                    onChange={(e) => setSearchCategories(e.target.value)}
                    placeholder="e.g., technology, politics"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Published After"
                    value={searchPublishedOn}
                    onChange={(e) => setSearchPublishedOn(e.target.value)}
                    placeholder="YYYY-MM-DD"
                    helperText="Filter by publication date"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleSearchNews}
                    disabled={searchLoading}
                    sx={{ mb: 2 }}
                  >
                    {searchLoading ? 'Searching...' : 'Search News'}
                  </Button>
                </Grid>
              </Grid>

              {/* Current Segment News Items */}
              <Typography variant="h6" sx={{ mb: 2 }}>
                Current News Items ({segmentNewsItems[currentSearchSegment?._id || '']?.length || 0})
              </Typography>
              <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
                {segmentNewsItems[currentSearchSegment?._id || '']?.map((item, index) => (
                  <Card key={item._id} sx={{ mb: 1, p: 1 }}>
                    <Typography variant="body2" fontWeight="bold" noWrap>
                      {item.article?.title || 'Unknown Article'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.article?.source?.name || 'Unknown Source'} • {new Date(item.article?.publishedAt || '').toLocaleDateString()}
                    </Typography>
                  </Card>
                )) || (
                  <Typography variant="body2" color="text.secondary">
                    No news items added yet
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Results Panel */}
            <Box sx={{ width: '60%', p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Search Results ({searchResults.length})
              </Typography>
              
              {searchLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <LinearProgress sx={{ flex: 1 }} />
                  <Typography variant="body2">Searching...</Typography>
                </Box>
              )}

              {searchResults.length > 0 ? (
                <Box sx={{ maxHeight: 'calc(90vh - 200px)', overflow: 'auto' }}>
                  {searchResults.map((article) => (
                    <Card key={'_id' in article ? article._id : article.url} sx={{ mb: 2, p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            {article.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {article.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip 
                              label={typeof article.source === 'string' ? article.source : article.source.name} 
                              size="small" 
                              variant="outlined"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {new Date('publishedAt' in article ? article.publishedAt : article.published_at).toLocaleDateString()}
                            </Typography>
                            {searchType === 'external' && (
                              <Chip 
                                label="External" 
                                size="small" 
                                color="primary"
                              />
                            )}
                          </Box>
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => currentSearchSegment && handleAddNewsToSegment(currentSearchSegment._id, article)}
                          sx={{ ml: 2 }}
                        >
                          Add to Segment
                        </Button>
                      </Box>
                    </Card>
                  ))}
                </Box>
              ) : searchQuery && !searchLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    No results found for "{searchQuery}"
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Try adjusting your search terms or filters
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography color="text.secondary">
                    Search for news articles to add to this segment
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Use the search panel on the left to find relevant news
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewsSearchModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Episode Dialog */}
      <Dialog open={openEpisodeDialog} onClose={handleCloseEpisodeDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit Episode</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Episode Title"
              value={episodeFormData.title}
              onChange={(e) => setEpisodeFormData({ ...episodeFormData, title: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={episodeFormData.description}
              onChange={(e) => setEpisodeFormData({ ...episodeFormData, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Episode Number"
                  type="number"
                  value={episodeFormData.episodeNumber || ''}
                  onChange={(e) => setEpisodeFormData({ 
                    ...episodeFormData, 
                    episodeNumber: e.target.value ? parseInt(e.target.value) : undefined 
                  })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Season Number"
                  type="number"
                  value={episodeFormData.seasonNumber || ''}
                  onChange={(e) => setEpisodeFormData({ 
                    ...episodeFormData, 
                    seasonNumber: e.target.value ? parseInt(e.target.value) : undefined 
                  })}
                />
              </Grid>
            </Grid>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={episodeFormData.status}
                label="Status"
                onChange={(e) => setEpisodeFormData({ ...episodeFormData, status: e.target.value as any })}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={episodeFormData.isPublic}
                  onChange={(e) => setEpisodeFormData({ ...episodeFormData, isPublic: e.target.checked })}
                />
              }
              label="Public Episode"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEpisodeDialog}>Cancel</Button>
          <Button onClick={handleEpisodeSubmit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message Snackbar */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSuccessMessage(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EpisodeDetail;
